import { Checkbox, Stack, Tooltip } from '@mui/material'
import type { MRT_Row, MRT_RowData, MRT_TableInstance } from 'material-react-table'
import { MRT_SelectCheckbox as MrtSelectCheckbox } from 'material-react-table'
import React from 'react'
import { useIntl } from 'react-intl'
import { useMrtRowSelectionSnapshot } from './mrtRowSelectionContext'

/**
 * MRT sizes its checkbox for its own 2.5rem-wide column and pulls it back with a negative margin to fit
 * there. In a content column that reads as a squashed box crowding the cell beside it, so the checkbox
 * is sized like any other inline control. Shared by both checkboxes so the header and the rows under it
 * cannot drift apart. flexShrink because it sits in a row Stack next to content that would squeeze it.
 */
const CHECKBOX_SX = { p: 0, m: 0, mr: 1, height: 'auto', width: 'auto', flexShrink: 0 } as const

/**
 * A cell in the column that carries the table's selection (see `hostsSelectionCheckbox`): the row's
 * checkbox drawn beside the cell's usual content, rather than in MRT's leading checkbox column.
 *
 * MRT's own checkbox rather than a bare one, so the column keeps the behaviour the built-in column
 * has: shift-click range selection and an accessible name. Only its geometry is overridden, which also
 * drops the table's muiSelectCheckboxProps sx - that indent is for MRT's own leading column. A row the
 * table can't select gets no checkbox, so the column itself needs no guard - `rowSelectionMode`
 * already says which rows take one.
 */
export const MrtSelectionCell = <T extends MRT_RowData>({
  row,
  staticRowIndex,
  table,
  children,
}: {
  row: MRT_Row<T>
  staticRowIndex?: number
  table: MRT_TableInstance<T>
  children: React.ReactNode
}): React.ReactNode => {
  useMrtRowSelectionSnapshot(row)
  if (!row.getCanSelect()) return children

  return (
    <Stack direction="row" alignItems="center">
      <MrtSelectCheckbox row={row} staticRowIndex={staticRowIndex} table={table} size="small" sx={CHECKBOX_SX} />
      {children}
    </Stack>
  )
}

export const MrtRowSelectCheckbox = <T extends MRT_RowData>({
  row,
  staticRowIndex,
  table,
}: {
  row: MRT_Row<T>
  staticRowIndex?: number
  table: MRT_TableInstance<T>
}): React.ReactElement => {
  useMrtRowSelectionSnapshot(row)
  return <MrtSelectCheckbox row={row} staticRowIndex={staticRowIndex} table={table} />
}

/**
 * The select-all checkbox in that column's header. Scoped to this page, like MRT's own: reaching every
 * page is what the toolbar's "select all N" button is for.
 *
 * Scoped, too, to the rows the table can actually select. The `*PageRows*` family filters on
 * `getCanSelect()` while the table-wide one counts every row in the model, so on a table that selects
 * only sub-rows the unselectable parents would hold this indeterminate forever: the count of selected
 * rows can never reach the count of rows. That is also why this isn't MRT's own checkbox, which reads
 * its indeterminate state table-wide.
 *
 * Unticking clears the whole selection rather than only this page, so a cross-page "select all" is
 * undone by the same click that appears to undo it.
 */
export const MrtSelectAllCheckbox = <T extends MRT_RowData>({
  table,
}: {
  table: MRT_TableInstance<T>
}): React.ReactElement => {
  const { formatMessage } = useIntl()
  // A page of parents with no children of their own - products with no mapped suppliers - has nothing to
  // tick, and a control that can't do anything shouldn't look like it can.
  const hasNothingToSelect = !table.getPaginationRowModel().flatRows.some(row => row.getCanSelect())

  return (
    <Tooltip
      title={hasNothingToSelect ? formatMessage({ id: 'resourceCollections.general.nothingToSelectOnPage' }) : ''}
    >
      {/* A disabled input fires no pointer events, so the tooltip listens on a wrapper instead. */}
      <span>
        <Checkbox
          size="small"
          sx={CHECKBOX_SX}
          inputProps={{ 'aria-label': table.options.localization.toggleSelectAll }}
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected()}
          disabled={hasNothingToSelect}
          onChange={(_event, checked) =>
            checked ? table.toggleAllPageRowsSelected(true) : table.resetRowSelection(true)
          }
        />
      </span>
    </Tooltip>
  )
}

/** That column's header: the select-all checkbox, then the header label it belongs to. */
export const MrtSelectionHeader = <T extends MRT_RowData>({
  table,
  label,
}: {
  table: MRT_TableInstance<T>
  label: string
}): React.ReactElement => (
  <Stack direction="row" alignItems="center">
    <MrtSelectAllCheckbox table={table} />
    <span>{label}</span>
  </Stack>
)
